import { datadogLogs } from "@datadog/browser-logs";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect } from "react";
import {
  useUpdateCustomerConsentMutation,
  useGetAccountQuery,
} from "src/graphql/generated/queries";
import { type GetAccountQuery } from "src/graphql/generated/types";
import { useCurrentAccount, useMyLogin, useSnackbar, useUuid } from "src/hooks";

import {
  type ConfidentialityInputs,
  ConfidentialityModal,
} from "./_components/ConfidentialityModal";
import { ConsentPlatformGate } from "./_components/consent-platform/consent-platform-gate";

type Props = {
  children: React.ReactNode;
};

export const CustomerWrapper = ({ children }: Props) => {
  const uuid = useUuid();
  const queryClient = useQueryClient();

  const login = useMyLogin();
  const { customer, quality, branch } = useCurrentAccount();

  const { enqueueSnackbar } = useSnackbar();

  const { mutate: updateConfidentiality, ...updateConfidentialityInfos } =
    useUpdateCustomerConsentMutation({
      onSuccess: ({ updateCustomerConsent: { confidentiality } }) => {
        enqueueSnackbar(
          "Vos paramètres de confidentialité ont bien été enregistrés",
          {
            variant: "success",
          },
        );

        const GET_ACCOUNT_KEY = useGetAccountQuery.getKey({ uuid });

        queryClient.setQueryData<GetAccountQuery>(
          GET_ACCOUNT_KEY,
          (cachedData) => {
            if (!cachedData) {
              return undefined;
            }

            return {
              __typename: "Query",
              account: {
                ...cachedData.account,
                customer: {
                  ...cachedData.account.customer,
                  confidentiality: {
                    ...cachedData.account.customer.confidentiality,
                    ...confidentiality,
                  },
                },
              },
            };
          },
        );
      },
      onError: () => {
        enqueueSnackbar(
          "Erreur lors de l'enregistrement de vos paramètres de confidentialité",
          {
            variant: "error",
          },
        );
      },
    });

  useEffect(() => {
    datadogLogs.setUser({
      id: customer.id,
      email: login ?? undefined,
      quality,
    });
    datadogLogs.setUserProperty("type", "customer");
  }, [customer, login, quality]);

  useEffect(() => {
    try {
      window.wisepops?.("properties", {
        quality,
        agencyId: branch?.agency.id,
        customerNumber: customer.number,
      });
    } catch {
      // Wisepops integration failure must not break the app
    }
  }, [quality, branch?.agency.id, customer.number]);

  const onSubmit = ({
    personalDataOptIn,
    partnersOffersInformationOptIn,
  }: ConfidentialityInputs) => {
    updateConfidentiality({
      customerId: customer.id,
      request: {
        personalData: personalDataOptIn,
        partnersOffersInformation: partnersOffersInformationOptIn,
        isFirstSubmit: true,
      },
    });
  };

  return (
    <>
      {children}
      <ConsentPlatformGate
        defaultForm={
          <ConfidentialityModal
            open={!customer.confidentiality.isFirstSubmit}
            loading={updateConfidentialityInfos.isLoading}
            onSubmit={onSubmit}
          />
        }
      />
    </>
  );
};
